import React, { useEffect, useState } from "react";
import { Box, Collapse, Tooltip, Typography, styled } from "@mui/material";
import { HomeCampusIcon } from "./IconHomeCampus";
import { MagnetIcon } from "./IconMagnet";
import { CalculatingFit } from "../center-panel/CalculatingFit";
import { useIsTruncated } from "../../hooks/useIsTruncated";
import { toSrc } from "../../utils/toSrc";
import type { DistrictProgramForHat, DistrictSchoolForHat } from "../../types";

import seeYourMatchesIconImport from "./see-your-matches.svg";

const seeYourMatchesIcon = toSrc(
  seeYourMatchesIconImport as string | { src: string },
);

const SCHOOLS_MAX_HEIGHT = 150;

// --- Styled components ---

const Heading = styled(Typography, {
  label: "DistrictHeading",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "24px",
  color: "#ffffff",
  textAlign: "center",
  width: "100%",
}));

const Prompt = styled(Typography, {
  label: "DistrictPrompt",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#ffffff",
  textAlign: "center",
  width: "100%",
}));

const SeeYourMatchesButton = styled("button", {
  label: "DistrictSeeYourMatches",
})(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  height: 48,
  minWidth: 64,
  padding: "0 16px",
  borderRadius: 8,
  border: "1px solid rgba(145, 158, 171, 0.32)",
  backgroundColor: "#ffffff",
  cursor: "pointer",
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "26px",
  color: "#1c252e",
  transition: "background-color 0.2s ease",
  "&:hover": { backgroundColor: "#f4f6f8" },
}));

const AccordionItem = styled(Box, {
  label: "DistrictAccordionItem",
})(() => ({
  backgroundColor: "#ffffff",
  borderRadius: 8,
  overflow: "hidden",
  width: "100%",
  flexShrink: 0,
  boxShadow: "0px 4px 16px 0px rgba(0,0,0,0.1)",
}));

const ProgramRow = styled(Box, {
  label: "DistrictProgramRow",
})(() => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: 8,
  cursor: "pointer",
}));

const ProgramName = styled(Typography, {
  label: "DistrictProgramName",
})(() => ({
  flex: 1,
  minWidth: 0,
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 500,
  fontSize: "13px",
  lineHeight: "18px",
  color: "rgba(0,0,0,0.8)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const Badge = styled(Box, {
  label: "DistrictBadge",
  shouldForwardProp: (prop) => prop !== "matchType",
})<{ matchType: "BEST_MATCH" | "CLOSE_MATCH" }>(({ matchType }) => ({
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 6px",
  height: 18,
  borderRadius: 6,
  backgroundColor: matchType === "BEST_MATCH" ? "#8e33ff" : "#ffab00",
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "10px",
  lineHeight: "20px",
  color: matchType === "BEST_MATCH" ? "#ffffff" : "#1c252e",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
}));

const SchoolsScroll = styled(Box, {
  label: "DistrictSchoolsScroll",
})(() => ({
  borderTop: "1px solid rgba(0,0,0,0.06)",
  margin: "0 10px",
  paddingTop: 5,
  paddingBottom: 8,
  maxHeight: SCHOOLS_MAX_HEIGHT,
  overflowY: "auto",
  "&::-webkit-scrollbar": { width: "5px" },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(0,0,0,0.18)",
    borderRadius: "3px",
  },
}));

const SchoolRow = styled(Box, {
  label: "DistrictSchoolRow",
})(() => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 2px",
}));

const SchoolName = styled(Typography, {
  label: "DistrictSchoolName",
})(() => ({
  flex: 1,
  minWidth: 0,
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "16px",
  color: "rgba(0,0,0,0.85)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const Chevron = ({ open }: { open: boolean }) => (
  <Box
    sx={{
      width: 16,
      height: 16,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transform: open ? "rotate(180deg)" : "none",
      transition: "transform 0.2s ease",
    }}
  >
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path
        d="M4.5 6L8 9.5L11.5 6"
        stroke="rgba(0,0,0,0.5)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

// Name that only shows a tooltip (the full name) when actually clipped.
const TruncatedLabel = ({
  name,
  component: Component,
}: {
  name: string;
  component: typeof ProgramName | typeof SchoolName;
}) => {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>();
  const label = <Component ref={ref}>{name}</Component>;
  if (!isTruncated) return label;
  return (
    <Tooltip title={name} placement="top">
      {label}
    </Tooltip>
  );
};

const SchoolTypeIcon = ({
  matchType,
}: {
  matchType: DistrictSchoolForHat["matchType"];
}) => (
  <Box
    sx={{
      width: 14,
      height: 14,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {matchType === "HOME_CAMPUS" ? <HomeCampusIcon /> : <MagnetIcon />}
  </Box>
);

// --- Main component ---

export const DistrictProgramsSection = ({
  programs,
  revealed = false,
  onRevealed,
}: {
  programs: DistrictProgramForHat[];
  /** When true, skip the "See Your Matches" button + animation and render results immediately. */
  revealed?: boolean;
  /** Fires once the calculating animation completes. */
  onRevealed?: () => void;
}) => {
  // Best matches first, close matches next.
  const sorted = [...programs].sort((a, b) => {
    if (a.matchType === b.matchType) return 0;
    return a.matchType === "BEST_MATCH" ? -1 : 1;
  });

  const [openId, setOpenId] = useState<string | null>(
    revealed ? (sorted[0]?.id ?? null) : null,
  );
  const [showMatches, setShowMatches] = useState(revealed);
  const [calculating, setCalculating] = useState(false);

  // Sync with the cached reveal flag if it loads after the initial render.
  useEffect(() => {
    if (revealed && !showMatches) {
      setShowMatches(true);
      setOpenId((prev) => prev ?? sorted[0]?.id ?? null);
    }
  }, [revealed, showMatches, sorted]);

  // Only one program open at a time.
  const toggle = (id: string) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        flex: "1 0 0",
        minHeight: 0,
      }}
    >
      <Box>
        <Heading>High School Programs</Heading>
        <Prompt>Where can I start preparing?</Prompt>
      </Box>

      {!showMatches && !calculating && (
        <Box
          sx={{
            display: "flex",
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SeeYourMatchesButton onClick={() => setCalculating(true)}>
            <img
              src={seeYourMatchesIcon}
              alt=""
              width={24}
              height={24}
              style={{ flexShrink: 0 }}
            />
            See Your Matches
          </SeeYourMatchesButton>
        </Box>
      )}

      {calculating && (
        <Box sx={{ pt: "10px" }}>
          <CalculatingFit
            label="Finding your matches…"
            onComplete={() => {
              setCalculating(false);
              setShowMatches(true);
              setOpenId(sorted[0]?.id ?? null);
              onRevealed?.();
            }}
          />
        </Box>
      )}

      {showMatches && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          {sorted.map((program) => {
            const open = openId === program.id;
            return (
              <AccordionItem key={program.id}>
                <ProgramRow onClick={() => toggle(program.id)}>
                  <TruncatedLabel name={program.name} component={ProgramName} />
                  <Badge matchType={program.matchType}>
                    {program.matchType === "BEST_MATCH"
                      ? "Best Match"
                      : "Close Match"}
                  </Badge>
                  <Chevron open={open} />
                </ProgramRow>
                <Collapse in={open} timeout={250} unmountOnExit>
                  {program.schools.length > 0 && (
                    <SchoolsScroll>
                      {program.schools.map((school) => (
                        <SchoolRow key={school.id}>
                          <SchoolTypeIcon matchType={school.matchType} />
                          <TruncatedLabel
                            name={school.name}
                            component={SchoolName}
                          />
                        </SchoolRow>
                      ))}
                    </SchoolsScroll>
                  )}
                </Collapse>
              </AccordionItem>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
